import {
  type ConvertedPiece,
  toTranscription,
  type WriteWord,
  writeBopomofoWord,
  writeGwoyeuWord,
  writeIpaWord,
  writeWadeGilesWord,
  writeYaleWord,
} from "@kensio/pinyinjs";

/**
 * The other ways of writing what the conversion worked out.
 *
 * Each is one call to `toTranscription` with a different word writer, which is
 * the whole of what separates them: the hard part — deciding which reading
 * each character takes, and where one word ends and the next begins — has
 * already happened, and none of these systems disagrees with pinyin about any
 * of it. 北京大学 is two words in all of them.
 */

/** One transcription system, as a panel on the page. */
export interface System {
  /** Matches `data-cb-transcription` in the template. */
  readonly id: string;
  readonly label: string;
  readonly write: WriteWord;
  /**
   * Whether the system writes capital letters.
   *
   * The three romanisations do, because they spell Chinese in the Latin
   * alphabet and inherit what that alphabet does with a proper noun. IPA does
   * not: its letters are symbols rather than an alphabet, and a capital `T` is
   * a different symbol rather than a larger `t`. Bopomofo is a script with no
   * case at all.
   */
  readonly capitals: boolean;
}

/**
 * The systems, in the order the pages introduce them.
 */
export const systems: readonly System[] = [
  {
    id: "bopomofo",
    label: "Bopomofo (注音符號)",
    write: writeBopomofoWord,
    capitals: false,
  },
  {
    id: "wade-giles",
    label: "Wade-Giles",
    write: writeWadeGilesWord,
    capitals: true,
  },
  { id: "yale", label: "Yale", write: writeYaleWord, capitals: true },
  {
    id: "gwoyeu",
    label: "Gwoyeu Romatzyh (國語羅馬字)",
    write: writeGwoyeuWord,
    capitals: true,
  },
  { id: "ipa", label: "IPA", write: writeIpaWord, capitals: false },
];

/** The system a panel is for, if it names one this site knows. */
export function systemById(id: string): System | undefined {
  return systems.find((system) => system.id === id);
}

/**
 * Write a conversion in one system.
 */
export function transcribe(
  pieces: readonly ConvertedPiece[],
  system: System,
): string {
  return toTranscription(pieces, system.write, { capitals: system.capitals });
}
